<template>
  <div
    :class="`border border-dashed ${
      errors.length !== 0 ? 'border-red-500' : 'border-gray-400'
    } rounded-md w-100 overflow-auto scroll-style bg-white h-72`"
  >
    <section
      id="empty-items"
      v-if="files.length === 0"
      class="flex justify-center items-center flex-col h-full gap-4"
      ref="image-upload"
      @drop.prevent="dropHandler($event, 'image-upload')"
      @dragover.prevent="dragOverHandler('image-upload')"
      @dragleave.prevent="dragLeaveHandler('image-upload')"
    >
      <span class="text-xl font-bold text-h-primary-500">
        {{
          $t("labels.generic", {
            pt: "Selecione os arquivos",
            en: "Select files",
          })
        }}
      </span>
      <v-icon name="md-uploadfile" :scale="3" class="text-gray-500" />
      <input
        id="file"
        type="file"
        :accept="acceptFiles"
        @change="addFiles"
        @click="resetInput($event)"
        hidden
      />
      <label for="file" class="btn-primary p-2 rounded-md cursor-pointer">
        {{ btnLabel }}
      </label>
    </section>

    <section
      id="with-items"
      v-else
      :class="`flex flex-wrap items-center h-full ${
        minFiles === 1 ? 'justify-center' : ''
      } gap-2 p-3`"
    >
      <div
        v-for="(file, i) in files"
        :key="i"
        :class="{ 'cursor-pointer': file?.id }"
        @click.prevent.stop="file?.id ? handleEventClick(file) : ''"
      >
        <div
          :class="`bg-gray-100 rounded-md relative flex items-center justify-center border-2 border-h-primary-700
            ${minFiles === 1 ? 'size-one' : 'size-default'}`"
        >
          <span
            id="remove-item"
            class="absolute -right-2 -top-2 bg-h-primary-500 rounded-full text-xs p-1 hover:scale-110"
            @click.prevent.stop="removeFiles(file?.id, i)"
          >
            <v-icon
              name="hi-solid-x-circle"
              class="text-white cursor-pointer"
            />
          </span>
          <span class="text-break mt-3 text-center">
            <v-icon
              :name="`md-${fileType(file?.type || file?.extension)}`"
              :scale="3"
              class="text-h-primary-500"
            />
            <p
              :class="`p-2 font-bold text-xs text-h-primary-500 line-clamp-2 ${
                minFiles === 1 ? 'span' : ''
              }`"
            >
              {{ file?.name }}
            </p>
          </span>
        </div>
      </div>
      <div id="add-more-items" class="flex items-center">
        <input
          id="file"
          type="file"
          :accept="acceptFiles"
          @change="addFiles"
          @click="resetInput($event)"
          hidden
        />
        <label
          for="file"
          class="cursor-pointer hover:scale-110 text-h-primary-500"
          v-if="files?.length < minFiles"
        >
          <v-icon name="hi-solid-plus-circle" :scale="2" />
        </label>
      </div>
    </section>
  </div>
  <Errors :errors="errors" />
</template>

<script lang="ts">
import { Messages } from "@/utils/messages.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { defineComponent, PropType } from "vue";
export default defineComponent({
  name: "dropzone",
  emits: ["handleEventClick", "onThumbnailView", "onRemoveThumbnailView"],
  props: {
    files: {
      type: Array as PropType<any>,
      default: [],
    },
    filesDeleted: {
      type: Array as PropType<any>,
    },
    minFiles: {
      type: Number as PropType<number>,
      default: 1,
    },
    acceptFiles: {
      type: String as PropType<string>,
      default: ".mp4,.pdf,.jpg,.jpeg,.png",
    },
    errors: {
      type: Array as PropType<any>,
      default: [],
    },
    info: {
      type: String as PropType<string>,
      default: "",
    },
    btnLabel: {
      type: String as PropType<string>,
      default: "Enviar",
    },
    limit: {
      type: Number,
      default: 471859200,
    },
  },
  methods: {
    handleEventClick(data: any): void {
      this.$emit("handleEventClick", data);
    },
    fileType(fileType: string): string {
      const justType = fileType?.split("/")?.pop();
      if (
        justType === "png" ||
        justType === "jpg" ||
        justType === "jpeg" ||
        justType === "svg+xml"
      ) {
        return "image-round";
      } else if (justType === "mp4" || justType === "webdm") {
        return "videolibrary";
      } else if (justType === "pdf") {
        return "pictureaspdf";
      } else {
        return "insertdrivefile";
      }
    },
    addFiles(event: any): void {
      event.preventDefault();
      if (this.files.length < this.minFiles) {
        const file: File = event.target.files[0];
        if (file.size <= this.limit) {
          this.checkFiles(file);
        } else {
          this.$toast.add(ToastFactory.error(Messages.MATERIAL_LIMIT_SIZE));
        }
      }
    },
    checkFiles(file: File): void {
      const type = file?.type?.split("/")[1];
      if (
        type === "jpg" ||
        type === "jpeg" ||
        type === "png" ||
        type === "pdf" ||
        type === "mp4" ||
        type === "x-mpegURL" ||
        type === "mp3"
      ) {
        this.files.push(file);
        this.$emit("onThumbnailView", file);
      } else {
        this.$toast.add(ToastFactory.warn(Messages.MATERIAL_TYPE_ERROR));
      }
    },
    removeFiles(fileId: any, index: number): void {
      this.$emit("onRemoveThumbnailView");
      if (fileId) {
        this.filesDeleted?.push(fileId);
        this.files.splice(index, 1);
      } else {
        this.files.splice(index, 1);
      }
    },
    dropHandler(event: any, refName: string) {
      event.preventDefault();
      event.stopPropagation();
      const droppedFiles = event.dataTransfer.files;
      if (droppedFiles.length > 0) {
        if (droppedFiles[0].size <= this.limit) {
          this.checkFiles(droppedFiles[0]);
        } else {
          this.$toast.add(ToastFactory.error(Messages.MATERIAL_LIMIT_SIZE));
        }
      }
      (this.$refs[refName] as HTMLElement).style.backgroundColor = "white";
    },
    dragOverHandler(refName: string) {
      (this.$refs[refName] as HTMLElement).style.backgroundColor =
        "rgba(128, 128, 128, 0.1)";
    },
    dragLeaveHandler(refName: string) {
      (this.$refs[refName] as HTMLElement).style.backgroundColor = "white";
    },
    resetInput(event: any): void {
      event.target.value = "";
    },
  },
});
</script>

<style scoped lang="scss" src="./style.scss" />
